import React, {useEffect, useRef, useState} from "react";
import {Redirect, useParams} from "react-router-dom";
import UserService from "../../services/user.service";
import "../../Print.css";
import {logOut} from "../../helpers/logout";
import {Button, Divider, Grid, Toolbar} from "@material-ui/core";
import ActionButton from "../form/input/ActionButton";
import Tooltip from "@material-ui/core/Tooltip";
import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import {makeStyles} from "@material-ui/core/styles";
import clsx from "clsx";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import GeneratedDocumentEmailAction from "./GeneratedDocumentEmailAction";
import {PDFExport} from "@progress/kendo-react-pdf";
import {getPdfExportProxyProps, savePdfWithEmbeddedImages} from "../../helpers/pdfExport";
import {NotificationManager} from "react-notifications";

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        width: "100%",
        backgroundColor: theme.palette.background.paper,
    },
    seeMore: {
        marginTop: theme.spacing(8),
    },
    paper: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
    },
    toolbarButtons: {
        "& .MuiFormControl-root": {
            margin: theme.spacing(1),
        },
    },
    marginTop: {
        marginTop: theme.spacing(2),
    },
    document: {
        padding: "22mm 18mm 14mm 18mm",
        boxSizing: "border-box",
        background: "#ffffff",
        color: "#000000",
        fontFamily: '"DejaVu Sans", "Arial", sans-serif',
        fontSize: 12,
        lineHeight: 1.28,
    },
    editHint: {
        margin: "0 0 12px 0",
        fontWeight: "bold",
        textAlign: "center",
        color: "#2c4767",
    },
    title: {
        textAlign: "center",
        fontWeight: "bold",
        margin: "0 0 13mm 0",
        lineHeight: 1.2,
    },
    sectionTitle: {
        textAlign: "center",
        fontWeight: "bold",
        margin: "11mm 0 6mm 0",
        lineHeight: 1.2,
    },
    centeredHint: {
        textAlign: "center",
        fontWeight: "bold",
        fontSize: 11,
        marginTop: "-1mm",
    },
    italicHint: {
        textAlign: "center",
        fontStyle: "italic",
        fontSize: 11,
        lineHeight: 1.25,
    },
    formRow: {
        display: "flex",
        alignItems: "baseline",
        minHeight: "5.4mm",
    },
    label: {
        whiteSpace: "nowrap",
        marginRight: 4,
    },
    field: {
        flex: 1,
        border: 0,
        borderBottom: "1px dotted #000",
        background: "transparent",
        fontFamily: '"DejaVu Sans", "Arial", sans-serif',
        fontSize: 12,
        lineHeight: "15px",
        padding: "0 2px",
        minWidth: 0,
    },
    shortField: {
        flex: "0 0 34mm",
    },
    mediumField: {
        flex: "0 0 72mm",
    },
    fullLineField: {
        width: "100%",
        border: 0,
        borderBottom: "1px dotted #000",
        background: "transparent",
        fontFamily: '"DejaVu Sans", "Arial", sans-serif',
        fontSize: 12,
        textAlign: "center",
        lineHeight: "17px",
        padding: "0 2px",
    },
    spacer: {
        height: "8mm",
    },
    largeSpacer: {
        height: "16mm",
    },
    footnote: {
        marginTop: "18mm",
    },
}));

const buildAddress = (funeral) => [funeral.ca_address, funeral.ca_zip, funeral.ca_city]
    .filter(Boolean)
    .join(", ");

const buildContact = (funeral) => [funeral.ca_phone, funeral.ca_email]
    .filter(Boolean)
    .join(", ");

const buildFuneralService = (user) => [user?.name, user?.address]
    .filter(Boolean)
    .join(", ");

const FetalRemainsApplicationGenerator = (props) => {
    const classes = useStyles();
    const fixedHeightPaper = clsx(classes.paper);
    const pdfRef = useRef(null);
    const funeralId = useParams();
    const userLocal = JSON.parse(localStorage.getItem("user")).id;

    const [funeral, setFuneral] = useState({
        _id: "",
        deceased: "",
        ca_email: "",
    });
    const [user, setUser] = useState(null);
    const [fields, setFields] = useState({
        parentName: "",
        parentAddress: "",
        parentIdCard: "",
        parentContact: "",
        pregnancyEndDate: "",
        weight: "",
        requestDate: "",
        parentSignature: "",
        healthcareProvider: "",
        handoverDate: "",
        supervisionOffice: "",
        funeralService: "",
        funeralServicePickupDate: "",
        funeralServiceRepresentative: "",
    });
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        UserService.getUser(userLocal).then(
            (response) => {
                setUser(response.data);
                setFields((currentFields) => ({
                    ...currentFields,
                    funeralService: buildFuneralService(response.data),
                }));
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                    NotificationManager.warning("Potrebné sa znova prihlásiť!", "Upozornenie", 5000);
                }
            }
        );

        UserService.getFuneral(funeralId.funeral_id).then(
            (response) => {
                setFuneral(response.data);
                setFields((currentFields) => ({
                    ...currentFields,
                    parentName: response.data.contracting_authority || "",
                    parentAddress: buildAddress(response.data),
                    parentIdCard: response.data.ca_id || "",
                    parentContact: buildContact(response.data),
                }));
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                    NotificationManager.warning("Potrebné sa znova prihlásiť!", "Upozornenie", 5000);
                }
            }
        );
    }, [funeralId.funeral_id, userLocal, props]);

    const handleChange = (event) => {
        const {name, value} = event.target;
        setFields({...fields, [name]: value});
    };

    const handleExport = () => {
        savePdfWithEmbeddedImages(pdfRef, (error) => {
            if (error) {
                NotificationManager.error("Nepodarilo sa exportovať PDF!", "Chyba", 5000);
            }
        });
    };

    const renderField = (name, className = "") => (
        <input
            name={name}
            onChange={handleChange}
            type="text"
            className={`${classes.field} ${className}`}
            value={fields[name]}
        />
    );

    const renderFullLineField = (name) => (
        <input
            name={name}
            onChange={handleChange}
            type="text"
            className={classes.fullLineField}
            value={fields[name]}
        />
    );

    const fileNameSuffix = (funeral.deceased || fields.parentName || "plod").replace(/\s/g, "");

    const showDocument = () => (
        <>
            <div className={classes.editHint}>Po KLIKNUTÍ viete dopisovať alebo meniť údaje.</div>
            <div className="document-preview-frame" style={{border: "1px solid"}}>
            <PDFExport
                ref={pdfRef}
                {...getPdfExportProxyProps()}
                paperSize="A4"
                scale={0.75}
                fileName={`ziadost-rodica-o-vydanie-plodu-${fileNameSuffix}`}
            >
                <div className={`a4-paper ${classes.document}`}>
                    <div className={classes.title}>
                        Žiadosť rodiča o vydanie potrateného ľudského plodu<br/>
                        alebo predčasne odňatého ľudského plodu na pochovanie
                    </div>

                    {renderFullLineField("parentName")}
                    <div className={classes.italicHint}>
                        (meno a priezvisko rodiča potrateného plodu alebo predčasne odňatého ľudského plodu)
                    </div>

                    <div className={classes.formRow}>
                        <span className={classes.label}>Adresa bydliska:</span>
                        {renderField("parentAddress")}
                    </div>
                    <div className={classes.formRow}>
                        <span className={classes.label}>Číslo OP:</span>
                        {renderField("parentIdCard", classes.mediumField)}
                    </div>
                    <div className={classes.formRow}>
                        <span className={classes.label}>Kontakt (telefón / e-mail)*):</span>
                        {renderField("parentContact")}
                    </div>
                    <div className={classes.centeredHint}>
                        týmto žiadam o vydanie potrateného ľudského plodu alebo predčasne odňatého ľudského plodu
                    </div>

                    <div className={classes.spacer}/>
                    <div className={classes.formRow}>
                        <span className={classes.label}>Dátum ukončenia tehotenstva:</span>
                        {renderField("pregnancyEndDate", classes.shortField)}
                    </div>
                    <div className={classes.formRow}>
                        <span className={classes.label}>Hmotnosť plodu*:</span>
                        {renderField("weight", classes.shortField)}
                        <span className={classes.label}>&nbsp; gramov</span>
                    </div>
                    <div className={classes.centeredHint}>na pochovanie poverenej pohrebnej službe.</div>

                    <div className={classes.spacer}/>
                    <div className={classes.formRow}>
                        <span className={classes.label}>Dátum spísania žiadosti:</span>
                        {renderField("requestDate", classes.shortField)}
                    </div>
                    <div className={classes.formRow}>
                        <span className={classes.label}>Podpis rodiča:</span>
                        {renderField("parentSignature", classes.mediumField)}
                    </div>
                    <div className={classes.formRow}>
                        <span className={classes.label}>Poskytovateľ zdravotnej starostlivosti:</span>
                        {renderField("healthcareProvider")}
                    </div>
                    <div className={classes.italicHint}>
                        (podpis ošetrujúceho zdravotníckeho pracovníka<br/>
                        a odtlačok pečiatky)
                    </div>

                    <div className={classes.largeSpacer}/>
                    <div className={classes.sectionTitle}>
                        Potratený ľudský plod alebo predčasne odňatý ľudský plod odovzdaný pracovisku úradu pre dohľad<br/>
                        na vykonanie pitvy
                    </div>

                    <div className={classes.formRow}>
                        <span className={classes.label}>Dátum prevzatia:</span>
                        {renderField("handoverDate", classes.shortField)}
                    </div>
                    <div className={classes.formRow}>
                        <span className={classes.label}>Za pracovisko úradu pre dohľad:</span>
                        {renderField("supervisionOffice")}
                    </div>
                    <div className={classes.italicHint}>(podpis a odtlačok pečiatky)</div>

                    <div className={classes.largeSpacer}/>
                    <div className={classes.sectionTitle}>
                        Potratený ľudský plod alebo predčasne odňatý ľudský plod odovzdaný poverenej<br/>
                        pohrebnej službe na pochovanie
                    </div>

                    {renderFullLineField("funeralService")}
                    <div className={classes.italicHint}>(názov a adresa pohrebnej služby)</div>

                    <div className={classes.spacer}/>
                    <div className={classes.formRow}>
                        <span className={classes.label}>Dátum prevzatia pohrebnou službou:</span>
                        {renderField("funeralServicePickupDate", classes.shortField)}
                    </div>
                    <div className={classes.formRow}>
                        <span className={classes.label}>Za poverenú pohrebnú službu:</span>
                        {renderField("funeralServiceRepresentative")}
                    </div>
                    <div className={classes.italicHint}>(meno, priezvisko, podpis a odtlačok pečiatky)</div>

                    <div className={classes.footnote}>*) Nepovinný údaj.</div>
                </div>
            </PDFExport>
            </div>
        </>
    );

    return (
        <Container maxWidth="lg" className={classes.seeMore}>
            <div className={classes.root}>
                <Paper className={fixedHeightPaper}>
                    <Grid container>
                        <Grid xs={12} sm={4} md={3} lg={3} item container>
                            <Toolbar>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className={classes.button}
                                    onClick={() => setSuccess(true)}
                                >
                                    Späť na detaily
                                </Button>
                            </Toolbar>
                        </Grid>
                        <Grid xs={12} sm={8} md={9} lg={9} item container justifyContent="flex-end">
                            <Toolbar className={`${classes.toolbarButtons} document-actions-toolbar`}>
                                <ActionButton color="primary">
                                    <Tooltip title="Pdf export">
                                        <PictureAsPdfIcon fontSize="small" onClick={handleExport}/>
                                    </Tooltip>
                                </ActionButton>
                                <GeneratedDocumentEmailAction
                                    pdfRef={pdfRef}
                                    funeral={funeral}
                                    user={user}
                                />
                            </Toolbar>
                        </Grid>
                    </Grid>

                    <Divider/>
                    <Grid
                        className={classes.marginTop}
                        container
                        spacing={0}
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {showDocument()}
                    </Grid>
                </Paper>
            </div>
            {success ? <Redirect to={{
                pathname: `/pohreb/${funeralId.funeral_id}/info`,
                state: {from: props.location, number: 5},
            }}/> : <></>}
        </Container>
    );
};

export default FetalRemainsApplicationGenerator;
